import type {
	VisibilityChangedPayload,
	BatchImpressionTrackingOptions,
} from '@/helpers/composables/useBatchImpressionTracking';
import { useBatchImpressionTracking } from '@/helpers/composables/useBatchImpressionTracking';

import { ClickoutUrl, setFinalUrlOnClickoutUrl, isClickoutUrl } from '@/helpers/clickout-helper';
import { toValue } from 'vue';
import { until } from '@vueuse/core';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { captureMessageForSentry } from '@/libraries/sentry';
import { useUserStore } from '@/stores/pinia/useUserStore';

export type OfferVisibilityChangedPayload = VisibilityChangedPayload<ClickoutUrl>;

export const observableOptions = {
	once: false,
	throttle: 300,
	intersection: {
		root: null,
		rootMargin: '0px 0px 0px 0px',
		threshold: 0.5, // 50% visibility of each element
	},
} as const;

export function useOfferImpressionTracking(options: BatchImpressionTrackingOptions = {}) {
	// let's derive a group id from title contexts if they're present.
	// this way, we can be sure that we send the title context with all offer impressions for that title.
	// it's problematic otherwise, if we're on the search page with different titles.

	const entityId = (toValue(options.additionalContexts) || [])
		.filter(context => context.__name === 'TitleContextGraphql')
		.map(context => (context as SnowplowTitleContextGraphql).jwEntityId)
		.find(Boolean);

	const apolloClient = useNuxtApp().$apolloClients.default!;
	const userStore = useUserStore();

	return useBatchImpressionTracking<ClickoutUrl>({
		category: 'offer',
		toContext: clickoutUrl => clickoutUrl.contexts.ClickoutContextGraphql,
		observableOptions: options.observableOptions ?? observableOptions,
		additionalContexts: options.additionalContexts,
		additionalProperties: options.additionalProperties,
		// Creates a promise, preventing the clickout event from firing until the promise is resolved when the offerImpressionUUID is created in the batch queue
		createUUIDPromise: clickoutUrl => clickoutUrl.createOfferImpressionUUIDPromise(),
		// Returns a function that sets the offerImpressionUUID on the ClickoutUrl from the batch queue, and resolves the promise
		getUuidSetterForElement: clickoutUrl => (uuid: string) => clickoutUrl.setOfferImpressionUUID(uuid),
		// groupId is needed in case we want to group batch impressions specifically
		groupId: options.groupId ?? entityId,
		onVisibilityCallback: async clickoutUrl => {
			if (isClickoutUrl(clickoutUrl)) {
				// allow mutations to be quickly turned off from k8s config
				const disableMutations = JW_CONFIG.OFFER_IMPRESSION_MUTATIONS?.disable;

				if (disableMutations) return;
				await until(() => userStore.jwId).toBeTruthy({ timeout: 500 });
				if (!userStore.jwId) return;
				try {
					await setFinalUrlOnClickoutUrl(clickoutUrl, apolloClient);
				} catch (error: any) {
					captureMessageForSentry(
						`[useOfferImpressionTracking]'}`,
						{ where: '[useOfferImpressionTracking.ts] onVisibilityCallback()', error },
						'error'
					);
				}
			}
		},
	});
}
